import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@h2/ui';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@h2/ui';
import { Input } from '@h2/ui';
import { Textarea } from '@h2/ui';
import { Button } from '@h2/ui';
import { Badge } from '@h2/ui';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { useWorkPresetStore, WorkPreset } from '../../stores/useWorkPresetStore';
import { useConfigurationStore } from '../../stores/useConfigurationStore';
import { ConfigurationScroll } from '../configuration/ConfigurationScroll';

const presetSchema = z.object({
  name: z.string().min(1, '이름을 입력해주세요').max(100, '이름은 100자 이내로 입력해주세요'),
  description: z.string().max(500, '설명은 500자 이내로 입력해주세요'),
  category: z.string().min(1, '카테고리를 선택해주세요'),
  tags: z.array(z.string()).min(1, '최소 1개의 태그를 추가해주세요'),
});

type PresetFormData = z.infer<typeof presetSchema>;

interface PresetFormModalProps {
  preset?: WorkPreset;
  onSave: () => void;
  onCancel: () => void;
}

const PRESET_CATEGORIES = [
  { value: 'translation', label: '번역' },
  { value: 'transcription', label: '전사' },
  { value: 'review', label: '검수' },
  { value: 'mixed', label: '혼합' },
  { value: 'custom', label: '커스텀' },
];

export const PresetFormModal: React.FC<PresetFormModalProps> = ({
  preset,
  onSave,
  onCancel
}) => {
  const { createPreset, updatePreset } = useWorkPresetStore();
  const { formData, configuration } = useConfigurationStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [currentStep, setCurrentStep] = useState(0); // 0: 메타데이터, 1: 설정 미리보기

  const form = useForm<PresetFormData>({
    resolver: zodResolver(presetSchema),
    defaultValues: {
      name: preset?.name || '',
      description: preset?.description || '',
      category: preset?.category || 'mixed',
      tags: preset?.tags || [],
    },
  });

  const watchedTags = form.watch('tags');

  const handleSubmit = async (data: PresetFormData) => {
    setIsSubmitting(true);
    try {
      if (preset) {
        // 수정
        await updatePreset(preset.id, {
          ...data,
          configData: preset.configData // 기존 설정 데이터 유지
        });
      } else {
        // 생성 - 현재 Configuration의 formData를 사용
        await createPreset({
          ...data,
          configData: {
            selectedPresetId: '',
            languageSettingsComponent: formData.languageSettingsComponent || {
              targetLanguages: [],
              tags: [],
              memo: ''
            },
            workflowComponent: formData.workflowComponent || {
              taskNodes: [],
              connections: []
            },
            workerAssignmentComponent: formData.workerAssignmentComponent || {
              assignmentMatrix: [],
              availableWorkers: [],
              availableGroups: []
            },
            finalConfirmationComponent: formData.finalConfirmationComponent || {
              jobs: [],
              expandedJobs: [],
              selectedLanguages: []
            }
          }
        });
      }
      onSave();
    } catch (error) {
      console.error('Failed to save preset:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !watchedTags.includes(tagInput.trim())) {
      form.setValue('tags', [...watchedTags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    form.setValue('tags', watchedTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const canProceedToPreview = () => {
    const { name, tags } = form.getValues();
    return name.trim() && tags.length > 0;
  };

  const handleNext = () => {
    if (currentStep === 0 && canProceedToPreview()) {
      setCurrentStep(1);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 1) {
      setCurrentStep(0);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      <div className="flex items-center space-x-4">
        <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
          currentStep === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
        }`}>
          1
        </div>
        <div className={`w-12 h-px ${currentStep >= 1 ? 'bg-primary' : 'bg-muted'}`} />
        <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
          currentStep === 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
        }`}>
          2
        </div>
      </div>
    </div>
  );

  const renderMetadataStep = () => (
    <Form {...form}>
      <div className="space-y-6">
        {/* 기본 정보 */}
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>프리셋 이름</FormLabel>
                <FormControl>
                  <Input placeholder="예: 한영 번역 표준" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>설명</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="이 프리셋의 용도와 특징을 설명하세요"
                    className="h-20"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>카테고리</FormLabel>
                <FormControl>
                  <select
                    className="w-full p-2 border rounded-md"
                    {...field}
                  >
                    {PRESET_CATEGORIES.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* 태그 */}
        <div className="space-y-3">
          <FormLabel>태그</FormLabel>
          <div className="flex gap-2">
            <Input
              placeholder="태그 입력 (엔터로 추가)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagInputKeyDown}
              className="flex-1"
            />
            <Button type="button" onClick={handleAddTag} disabled={!tagInput.trim()}>
              추가
            </Button>
          </div>
          
          {watchedTags.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {watchedTags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-sm">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <FormMessage />
        </div>

        {/* 현재 설정 미리보기 */}
        {!preset && (
          <div className="space-y-2">
            <FormLabel>포함될 설정</FormLabel>
            <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
              <div>• 언어: {formData.languageSettingsComponent?.targetLanguages?.length || 0}개</div>
              <div>• 작업 단계: {formData.workflowComponent?.taskNodes?.length || 0}개</div>
              <div>• 작업자 배정: {formData.workerAssignmentComponent ? '설정됨' : '미설정'}</div>
            </div>
          </div>
        )}
      </div>
    </Form>
  );

  const renderPreviewStep = () => (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        아래 설정들이 프리셋에 포함됩니다. 확인 후 저장하세요.
      </div>
      
      {/* 복합설정 UI 미리보기 */}
      {configuration && (
        <div className="border rounded-lg max-h-96 overflow-y-auto">
          <ConfigurationScroll />
        </div>
      )}
    </div>
  );

  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {preset ? '프리셋 편집' : '새 프리셋 생성'}
          </DialogTitle>
          <DialogDescription>
            {currentStep === 0 
              ? '프리셋의 기본 정보를 입력하세요'
              : '포함될 설정을 확인하고 저장하세요'
            }
          </DialogDescription>
        </DialogHeader>

        {renderStepIndicator()}

        <div className="flex-1 overflow-y-auto">
          {currentStep === 0 ? renderMetadataStep() : renderPreviewStep()}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            취소
          </Button>
          
          {currentStep === 0 ? (
            <Button 
              type="button" 
              onClick={handleNext}
              disabled={!canProceedToPreview()}
            >
              다음 <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={handlePrevious}>
                <ChevronLeft className="mr-1 h-4 w-4" /> 이전
              </Button>
              <Button 
                onClick={() => form.handleSubmit(handleSubmit)()}
                disabled={isSubmitting}
              >
                {isSubmitting ? '저장 중...' : (preset ? '수정' : '생성')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
